import type { z } from "zod";
import { RequestFailed } from "./api.js";

/**
 * The one server-sent-event reader.
 *
 * Two endpoints stream now — asking a question and deriving a PDF layout — and
 * they must not grow two frame parsers between them. A frame boundary is the
 * kind of thing that is subtly wrong in exactly one copy, and the interesting
 * copy is always the one nobody remembered to fix (`ai/output.ts` makes the
 * same argument about `safeJson`).
 *
 * Every frame is parsed with the same schema the server encoded it with, so a
 * shape the API could not have produced fails here, at the boundary, rather
 * than as `undefined` inside a component (docs/05 §5.7, §5.8).
 *
 * Not `EventSource`: that is GET-only and cannot send a cookie-authenticated
 * POST body. `fetch` with a reader does the same job, and gives an
 * `AbortSignal` that actually tears the connection down at the other end.
 */
export async function* eventStream<S extends z.ZodType>(
  schema: S,
  path: string,
  body: unknown,
  signal: AbortSignal,
): AsyncGenerator<z.infer<S>> {
  let response: Response;

  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new RequestFailed("network", "Could not reach the server. Check your connection.", 0);
  }

  if (!response.ok || !response.body) {
    // The failures that happen before the stream opens — no session, an id that
    // is not yours, a deployment with no key — are ordinary HTTP errors and are
    // read as such. Both streaming routes are careful to decide all of those
    // before their first frame, so this stays a real status code.
    throw await readError(response);
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += value;

      // Frames are separated by a blank line. A partial frame stays in the
      // buffer until the rest of it arrives: TCP does not respect message
      // boundaries and a chunk can split one anywhere.
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf("\n\n");

        const data = frame
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim())
          .join("");
        if (data === "") continue;

        const parsed = schema.safeParse(JSON.parse(data));
        if (!parsed.success) {
          // Same rule as every other response: a shape the client cannot read
          // is a bug, and it is loud rather than silent.
          console.error("Stream event did not match its schema", parsed.error.issues);
          throw new RequestFailed(
            "unreadable",
            "The server sent something this version of the app cannot read. Reload the page.",
            200,
          );
        }

        yield parsed.data as z.infer<S>;
      }
    }
  } finally {
    // Releasing the lock lets the abort actually tear the connection down,
    // which is what stops the model call server-side.
    reader.releaseLock();
  }
}

async function readError(response: Response): Promise<RequestFailed> {
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string } };
    if (body.error?.message) {
      return new RequestFailed(
        (body.error.code ?? "internal") as "internal",
        body.error.message,
        response.status,
      );
    }
  } catch {
    // Fall through to the generic message: an unreadable error is still one.
  }

  return new RequestFailed(
    "internal",
    "Something went wrong at our end. The error has been logged.",
    response.status,
  );
}
